import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const MediaCenter = ({ user }) => {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: 'Root' }]);
  
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  
  const [showUpload, setShowUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [showComments, setShowComments] = useState(false);
  const [selectedFileForComments, setSelectedFileForComments] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [initializing, setInitializing] = useState(false);
  
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  // Check if user is admin
  const isAdmin = user?.role?.type === 'admin' || 
                  user?.role?.name === 'Administrator' || 
                  user?.email === 'admin@velocityairportsolutions.com';

  useEffect(() => {
    fetchFolders();
    fetchFiles(currentFolder);
  }, [currentFolder]);

  const fetchFolders = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/media/folders`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setFolders(response.data);
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  const fetchFiles = async (folderId) => {
    try {
      const params = folderId ? { folder_id: folderId } : {};
      const response = await axios.get(`${BACKEND_URL}/api/media/files`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params
      });
      setFiles(response.data);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const handleCreateFolder = async () => {
    try {
      const formData = new FormData();
      formData.append('name', newFolderName);
      if (currentFolder) {
        formData.append('parent_folder_id', currentFolder);
      }

      await axios.post(`${BACKEND_URL}/api/media/folders`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setNewFolderName('');
      setShowNewFolder(false);
      fetchFolders();
    } catch (error) {
      alert('Error creating folder: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Are you sure you want to delete this folder?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/media/folders/${folderId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchFolders();
    } catch (error) {
      alert('Error deleting folder: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUploadFile = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (currentFolder) {
        formData.append('folder_id', currentFolder);
      }

      await axios.post(`${BACKEND_URL}/api/media/files`, formData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSelectedFile(null);
      setShowUpload(false);
      fetchFiles(currentFolder);
    } catch (error) {
      alert('Error uploading file: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId, uploadedBy) => {
    // Check if user can delete this file
    if (!isAdmin && uploadedBy !== user?.id) {
      alert('You can only delete your own files');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/media/files/${fileId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchFiles(currentFolder);
    } catch (error) {
      alert('Error deleting file: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleOpenFolder = (folder) => {
    setCurrentFolder(folder.id);
    setBreadcrumbs([...breadcrumbs, { id: folder.id, name: folder.name }]);
  };

  const handleBreadcrumbClick = (index) => {
    const folder = breadcrumbs[index];
    setCurrentFolder(folder.id);
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
  };

  const handleInitDefaultFolders = async () => {
    setInitializing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/media/init-default-folders`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      alert(response.data.message);
      fetchFolders();
    } catch (error) {
      alert('Error initializing folders: ' + (error.response?.data?.detail || error.message));
    } finally {
      setInitializing(false);
    }
  };

  const fetchComments = async (fileId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/media/files/${fileId}/comments`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const formData = new FormData();
      formData.append('comment', newComment);

      await axios.post(`${BACKEND_URL}/api/media/files/${selectedFileForComments.id}/comments`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setNewComment('');
      fetchComments(selectedFileForComments.id);
    } catch (error) {
      alert('Error adding comment: ' + error.message);
    }
  };

  const handleDeleteComment = async (commentId, commentUserId) => {
    if (!isAdmin && commentUserId !== user?.id) {
      alert('You can only delete your own comments');
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/media/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchComments(selectedFileForComments.id);
    } catch (error) {
      alert('Error deleting comment: ' + error.message);
    }
  };

  const isImageFile = (fileType) => {
    return ['jpg', 'jpeg', 'png', 'gif'].includes(fileType.toLowerCase());
  };

  const handlePreviewImage = (file) => {
    setPreviewImage(file);
    setShowImagePreview(true);
  };

  const handleOpenComments = (file) => {
    setSelectedFileForComments(file);
    setShowComments(true);
    fetchComments(file.id);
  };

  const getCurrentFolders = () => {
    return folders.filter(f => f.parent_folder_id === currentFolder);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getFileIcon = (fileType) => {
    const icons = {
      pdf: '📄',
      doc: '📝', docx: '📝',
      xls: '📊', xlsx: '📊',
      ppt: '📊', pptx: '📊',
      jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️',
      txt: '📃'
    };
    return icons[fileType] || '📎';
  };

  return (
    <div className="space-y-4">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={index}>
            <button
              onClick={() => handleBreadcrumbClick(index)}
              className="text-blue-600 hover:underline"
            >
              {crumb.name}
            </button>
            {index < breadcrumbs.length - 1 && <span>/</span>}
          </React.Fragment>
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button onClick={() => setShowNewFolder(true)}>
          📁 New Folder
        </Button>
        <Button onClick={() => setShowUpload(true)}>
          ⬆️ Upload File
        </Button>
        {isAdmin && folders.length === 0 && (
          <Button variant="outline" onClick={handleInitDefaultFolders} disabled={initializing}>
            {initializing ? 'Creating...' : '🔧 Setup Default Folders'}
          </Button>
        )}
      </div>

      {/* Folders Grid */}
      {getCurrentFolders().length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Folders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {getCurrentFolders().map(folder => (
                <div
                  key={folder.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                >
                  <div onClick={() => handleOpenFolder(folder)}>
                    <div className="text-4xl mb-2">📁</div>
                    <div className="font-medium truncate">{folder.name}</div>
                  </div>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="destructive"
                      className="mt-2 w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder.id);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Files Grid */}
      <Card>
        <CardHeader>
          <CardTitle>Files</CardTitle>
          <CardDescription>
            {files.length} file{files.length !== 1 ? 's' : ''} in this folder
          </CardDescription>
        </CardHeader>
        <CardContent>
          {files.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No files in this folder. Upload your first file!</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {files.map(file => (
                <div key={file.id} className="border rounded-lg p-4 hover:bg-gray-50">
                  {/* Show image preview thumbnail for image files */}
                  {isImageFile(file.file_type) ? (
                    <div 
                      className="mb-2 cursor-pointer"
                      onClick={() => handlePreviewImage(file)}
                    >
                      <img 
                        src={file.file_url} 
                        alt={file.name}
                        className="w-full h-40 object-cover rounded border"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="text-4xl mb-2">{getFileIcon(file.file_type)}</div>
                  )}
                  <div className="font-medium truncate mb-1">{file.name}</div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>Size: {formatFileSize(file.file_size)}</div>
                    <div>By: {file.uploaded_by_name || 'Unknown'}</div>
                    <div>{new Date(file.uploaded_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    {isImageFile(file.file_type) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePreviewImage(file)}
                        className="flex-1"
                      >
                        👁️ Preview
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(file.file_url, '_blank')}
                      className={isImageFile(file.file_type) ? '' : 'flex-1'}
                    >
                      📥 Download
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenComments(file)}
                    >
                      💬
                    </Button>
                    {(isAdmin || file.uploaded_by === user?.id) && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteFile(file.id, file.uploaded_by)}
                      >
                        🗑️
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Folder Dialog */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Folder Name</Label>
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Enter folder name"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNewFolder(false)}>Cancel</Button>
              <Button onClick={handleCreateFolder} disabled={!newFolderName.trim()}>
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload File Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload File</DialogTitle>
            <DialogDescription>
              Supported formats: PDF, Word, Excel, PowerPoint, Images (JPG, PNG, GIF)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Choose File</Label>
              <Input
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.txt"
              />
              {selectedFile && (
                <p className="text-sm text-gray-600 mt-2">
                  Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
              <Button onClick={handleUploadFile} disabled={!selectedFile || uploading}>
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Comments Dialog */}
      <Dialog open={showComments} onOpenChange={setShowComments}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Comments: {selectedFileForComments?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Comments List */}
            <div className="max-h-96 overflow-y-auto space-y-3">
              {comments.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No comments yet. Be the first to comment!</p>
              ) : (
                comments.map(comment => (
                  <div key={comment.id} className="border rounded p-3 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-semibold">{comment.user_name || 'Unknown'}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          {new Date(comment.created_at).toLocaleString()}
                        </span>
                      </div>
                      {(isAdmin || comment.user_id === user?.id) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteComment(comment.id, comment.user_id)}
                        >
                          🗑️
                        </Button>
                      )}
                    </div>
                    <p className="text-sm">{comment.comment}</p>
                  </div>
                ))
              )}
            </div>

            {/* Add Comment */}
            <div className="border-t pt-4">
              <Label>Add Comment</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                />
                <Button onClick={handleAddComment} disabled={!newComment.trim()}>
                  Post
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={showImagePreview} onOpenChange={setShowImagePreview}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{previewImage?.name}</DialogTitle>
            <DialogDescription>
              {previewImage && (
                <span>
                  {formatFileSize(previewImage.file_size)} • 
                  Uploaded by {previewImage.uploaded_by_name} • 
                  {new Date(previewImage.uploaded_at).toLocaleDateString()}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {previewImage && (
              <div className="flex justify-center bg-gray-100 rounded-lg p-4">
                <img 
                  src={previewImage.file_url} 
                  alt={previewImage.name}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => handleOpenComments(previewImage)}
              >
                💬 Comments
              </Button>
              <Button
                onClick={() => window.open(previewImage?.file_url, '_blank')}
              >
                📥 Download Full Size
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
